import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getEduProProduct } from '../services/api';
import { EdFiLogo } from '../components/EdFiLogo';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { amortize, downloadAmortizationPdf } from '../lib/eduproPdf';
import {
  ArrowRight, ArrowLeft, Check, Globe, Clock, Percent, BadgeCheck,
  Users, ShieldCheck, GraduationCap, Briefcase, Plane, Sparkles, CreditCard, Download
} from 'lucide-react';

const COUNTRIES = [
  { code: 'MX', name: 'México', flag: '🇲🇽', currency: 'MXN', locale: 'es-MX' },
  { code: 'CO', name: 'Colombia', flag: '🇨🇴', currency: 'COP', locale: 'es-CO' },
];

const FX_FALLBACK = { MXN: 18.5, COP: 4000 };
const TERMS = [12, 18, 24, 36];
const PRICE_USD = 1800;

const PROGRAMS = [
  { name: 'Hospitality', icon: GraduationCap, detail: 'Hotelería, turismo y gestión de servicios · 6,5 meses' },
  { name: 'Digital Business', icon: Briefcase, detail: 'Negocios digitales, marketing y e-commerce · 1 año' },
  { name: 'Logística', icon: Plane, detail: 'Cadena de suministro y operaciones · 1 año' },
  { name: 'Sociosanitario', icon: Users, detail: 'Cuidado de personas y servicios sociales · 7 meses' },
];

const EduProProductPage = () => {
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [country, setCountry] = useState('MX');
  const [term, setTerm] = useState(24);
  const [rate, setRate] = useState(3.0); // % monthly, variable 2.5 - 4.0

  useEffect(() => {
    getEduProProduct().then((r) => setProduct(r.data)).catch(() => {});
  }, []);

  const cn = COUNTRIES.find((c) => c.code === country);
  const currency = cn.currency;
  const locale = cn.locale;
  const fx = (product?.fx_rates?.[currency]) || FX_FALLBACK[currency];
  const principal = Math.round(PRICE_USD * fx);

  const calc = useMemo(() => amortize(principal, term, rate / 100), [principal, term, rate]);

  const fmt = (v) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: 0 }).format(v || 0);

  const goApply = () => navigate('/edupro/apply');

  const handleDownloadPdf = () => {
    downloadAmortizationPdf({
      principal, currency, locale, termMonths: term, monthlyRate: rate / 100, priceUsd: PRICE_USD,
      schedule: calc.schedule, payment: calc.payment, total: calc.total, interest: calc.interest,
      title: 'Simulación EduPro', subtitle: `Financiamiento de ${PRICE_USD.toLocaleString('en-US')} USD · ${cn.name}`,
    });
  };

  const perks = [
    { icon: CreditCard, title: '1,800 USD por alumno', desc: 'Financiado en tu moneda local (MXN o COP).' },
    { icon: Clock, title: 'Plazo 18 a 24 meses', desc: 'Elige el plazo que mejor se ajuste a ti.' },
    { icon: Percent, title: 'Interés 2.5% – 4% mensual', desc: 'Tasa según tu perfil y el de tu aval.' },
    { icon: BadgeCheck, title: 'Sin penalización', desc: 'Liquida por anticipado sin comisiones extra.' },
    { icon: Users, title: 'Con aval / co-deudor', desc: 'Tu aval respalda y mejora tus condiciones.' },
    { icon: Globe, title: 'México y Colombia', desc: 'Onboarding 100% digital para ambos países.' },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Nav */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <EdFiLogo size="md" linkTo="/" />
          <Link to="/" className="text-slate-500 hover:text-[#0A2463] text-sm flex items-center gap-1" data-testid="edupro-back-link">
            <ArrowLeft className="w-4 h-4" /> Volver
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-[#0A2463] text-white py-12 sm:py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
          <div className="inline-flex items-center gap-2 bg-[#E91E8C]/20 border border-[#E91E8C]/40 rounded-full px-4 py-1.5 mb-6">
            <Sparkles className="w-4 h-4 text-[#E91E8C]" />
            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-white/90">Nueva alianza estratégica</span>
          </div>
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="flex items-center gap-3 sm:gap-4 mb-6">
                <span className="text-2xl font-black text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Ed.Fi</span>
                <span className="text-white/40 text-2xl">×</span>
                <div className="bg-white rounded-xl px-3 sm:px-4 py-2">
                  <img src="/edupro-logo.png" alt="EduPro Global Education" className="h-7 sm:h-8 w-auto" data-testid="edupro-hero-logo" />
                </div>
              </div>
              <h1 className="text-3xl sm:text-5xl font-black tracking-tight leading-[1.08] mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Estudia, trabaja y vive en España con EduPro
              </h1>
              <p className="text-white/70 text-base sm:text-lg mb-8 max-w-lg">
                Financia tu programa internacional de <span className="text-white font-semibold">1,800 USD</span> con Ed.Fi.
                Pagos flexibles a 18–24 meses, en pesos mexicanos o colombianos.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={goApply} className="bg-[#E91E8C] hover:bg-[#c21473] text-white px-7 py-6 text-base rounded-xl" data-testid="edupro-apply-cta">
                  Solicitar sin crear cuenta <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <a href="https://eduproglobaleducation.com/" target="_blank" rel="noreferrer">
                  <Button variant="outline" className="w-full border-white/30 text-white bg-transparent hover:bg-white hover:text-[#0A2463] px-7 py-6 text-base rounded-xl">
                    Conocer EduPro
                  </Button>
                </a>
              </div>
            </div>

            {/* Simulator card */}
            <div className="bg-white rounded-3xl p-5 sm:p-8 text-slate-900 shadow-2xl" data-testid="edupro-calculator">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-semibold text-slate-500">Simulador de financiamiento</p>
                <span className="text-[11px] bg-[#0A2463]/5 text-[#0A2463] font-semibold px-2 py-1 rounded-full">1,800 USD</span>
              </div>

              <div className="mb-4">
                <p className="text-xs text-slate-500 mb-2">País / moneda</p>
                <div className="grid grid-cols-2 gap-2">
                  {COUNTRIES.map((c) => (
                    <button
                      key={c.code}
                      onClick={() => setCountry(c.code)}
                      className={`flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-medium transition-colors ${country === c.code ? 'border-[#0A2463] bg-[#0A2463] text-white' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                      data-testid={`calc-country-${c.code}`}
                    >
                      <span className="text-lg">{c.flag}</span> {c.currency}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-slate-500">Plazo</p>
                  <span className="text-xs font-semibold text-[#0A2463]">{term} meses</span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {TERMS.map((t) => (
                    <button
                      key={t}
                      onClick={() => setTerm(t)}
                      className={`py-2 rounded-xl border text-sm font-medium transition-colors ${term === t ? 'border-[#E91E8C] bg-[#E91E8C]/10 text-[#E91E8C]' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                      data-testid={`calc-term-${t}`}
                    >
                      {t}m
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-5">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-slate-500">Tasa mensual</p>
                  <span className="text-xs font-semibold text-[#E91E8C]" data-testid="calc-rate-value">{rate.toFixed(1)}%</span>
                </div>
                <Slider
                  min={2.5} max={4} step={0.1} value={[rate]}
                  onValueChange={(v) => setRate(v[0])}
                  data-testid="calc-rate-slider"
                />
                <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                  <span>2.5%</span><span>4.0%</span>
                </div>
              </div>

              <div className="bg-[#FAFAFA] rounded-2xl p-4 sm:p-5 border border-slate-100">
                <div className="flex items-end justify-between mb-3">
                  <div>
                    <p className="text-xs text-slate-500">Pago mensual estimado</p>
                    <p className="text-2xl sm:text-3xl font-black text-[#0A2463]" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="calc-monthly">
                      {fmt(calc.payment)}
                    </p>
                  </div>
                  <span className="text-xs bg-emerald-100 text-emerald-700 font-semibold px-2 py-1 rounded-full">{rate.toFixed(1)}% mensual</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm border-t border-slate-200 pt-3">
                  <div>
                    <p className="text-slate-400 text-[11px]">Financiado</p>
                    <p className="font-semibold text-xs sm:text-sm" data-testid="calc-principal">{fmt(principal)}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-[11px]">Total</p>
                    <p className="font-semibold text-xs sm:text-sm">{fmt(calc.total)}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-[11px]">Intereses</p>
                    <p className="font-semibold text-xs sm:text-sm">{fmt(calc.interest)}</p>
                  </div>
                </div>
              </div>

              <Button onClick={handleDownloadPdf} variant="outline" className="w-full mt-4 border-[#0A2463]/20 text-[#0A2463] hover:bg-[#0A2463]/5 rounded-xl py-5" data-testid="calc-download-pdf">
                <Download className="w-4 h-4 mr-2" /> Descargar tabla de amortización (PDF)
              </Button>
              <p className="text-[11px] text-slate-400 mt-3 text-center">Simulación informativa. Tasa final 2.5%–4% según verificación de perfil y aval. Sin penalización por pago anticipado.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Perks */}
      <section className="py-14 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#E91E8C] mb-3">El producto EduPro</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A2463] tracking-tight mb-10 max-w-2xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Condiciones claras, sin letras chiquitas
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {perks.map((p, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 p-6 hover:-translate-y-1 hover:shadow-lg transition-all bg-white">
                <div className="w-11 h-11 rounded-xl bg-[#0A2463] flex items-center justify-center mb-4">
                  <p.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-bold text-slate-900 mb-1">{p.title}</h3>
                <p className="text-sm text-slate-500">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs */}
      <section className="py-14 sm:py-16 bg-[#FAFAFA] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-8">
            <img src="/edupro-logo.png" alt="EduPro" className="h-8 sm:h-9 w-auto" />
            <span className="text-slate-300">|</span>
            <p className="text-slate-500 text-xs sm:text-sm">Programas acreditados en Madrid, España</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {PROGRAMS.map((p, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6">
                <p.icon className="w-7 h-7 sm:w-8 sm:h-8 text-[#E91E8C] mb-4" />
                <h3 className="font-bold text-[#0A2463] mb-1 text-sm sm:text-base">{p.name}</h3>
                <p className="text-[11px] sm:text-xs text-slate-500">{p.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements + CTA */}
      <section className="py-14 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
            <div className="rounded-3xl border border-slate-200 p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck className="w-5 h-5 text-[#0A2463]" />
                <h3 className="font-bold text-[#0A2463] text-lg">Qué necesitas</h3>
              </div>
              <ul className="space-y-3 text-sm text-slate-600">
                {[
                  'Ser mayor de edad (México o Colombia)',
                  'Identificación oficial vigente (INE o Cédula)',
                  'Selfie para prueba de vida y biometría',
                  'Comprobante de domicilio',
                  'Un aval / co-deudor que también complete su verificación',
                ].map((r, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" /> {r}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl bg-[#0A2463] text-white p-6 sm:p-8 flex flex-col justify-center">
              <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Listo para dar el paso</h3>
              <p className="text-white/70 text-sm mb-6">Empieza tu solicitud sin crear cuenta. Verificación de identidad en minutos, 100% digital, para ti y tu aval.</p>
              <Button onClick={goApply} className="bg-[#E91E8C] hover:bg-[#c21473] text-white px-6 py-6 text-base rounded-xl w-full" data-testid="edupro-apply-cta-bottom">
                Iniciar mi solicitud EduPro <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EduProProductPage;
